import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './ExperimentTimeSeries.css';

const VARIANT_COLORS = ['#8B6F4E', '#6B8E6B', '#B8860B', '#708090', '#CD853F'];

function ExperimentTimeSeries({ experimentId, variants }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('conversion_rate');
  const [tooltip, setTooltip] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/experiments/${experimentId}/timeseries`);
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch timeseries:', err);
        setError('Failed to load timeline data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [experimentId]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !data) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = 320;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, width, height);

    const padding = { top: 20, right: 20, bottom: 50, left: 60 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;

    // Collect all dates and values
    const series = data.series || [];
    if (series.length === 0) return;

    const allDates = [...new Set(series.flatMap((s) => (s.points || []).map((p) => p.date)))].sort();
    if (allDates.length === 0) return;

    let allValues = series.flatMap((s) => (s.points || []).map((p) => p[metric] || 0));
    let maxVal = Math.max(...allValues, 0.001);
    if (metric === 'conversion_rate') maxVal = Math.min(maxVal * 1.2, 1);
    const minVal = 0;

    const xScale = (i) => padding.left + (i / Math.max(allDates.length - 1, 1)) * chartW;
    const yScale = (v) => padding.top + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

    // Grid lines
    ctx.strokeStyle = '#f0ede8';
    ctx.lineWidth = 1;
    const gridLines = 5;
    for (let i = 0; i <= gridLines; i++) {
      const y = padding.top + (i / gridLines) * chartH;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      const val = maxVal - (i / gridLines) * (maxVal - minVal);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      if (metric === 'conversion_rate') {
        ctx.fillText((val * 100).toFixed(1) + '%', padding.left - 8, y + 4);
      } else {
        ctx.fillText('$' + val.toFixed(0), padding.left - 8, y + 4);
      }
    }

    // X-axis labels
    ctx.fillStyle = '#9ca3af';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    const labelInterval = Math.max(1, Math.floor(allDates.length / 8));
    allDates.forEach((date, i) => {
      if (i % labelInterval === 0 || i === allDates.length - 1) {
        const x = xScale(i);
        const d = new Date(date);
        const label = `${d.getMonth() + 1}/${d.getDate()}`;
        ctx.fillText(label, x, height - padding.bottom + 20);
      }
    });

    // Draw lines
    series.forEach((s, sIdx) => {
      const points = s.points || [];
      if (points.length === 0) return;

      const color = VARIANT_COLORS[sIdx % VARIANT_COLORS.length];
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      points.forEach((p, pIdx) => {
        const dateIdx = allDates.indexOf(p.date);
        const x = xScale(dateIdx);
        const y = yScale(p[metric] || 0);
        if (pIdx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.stroke();

      // Draw dots
      points.forEach((p) => {
        const dateIdx = allDates.indexOf(p.date);
        const x = xScale(dateIdx);
        const y = yScale(p[metric] || 0);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    });

    // Store layout for tooltip calculation
    canvas._chartLayout = { padding, chartW, chartH, allDates, series, xScale, yScale, maxVal, minVal };
  }, [data, metric]);

  useEffect(() => {
    drawChart();
    const handleResize = () => drawChart();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawChart]);

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas._chartLayout) {
      setTooltip(null);
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const { padding, allDates, series, xScale } = canvas._chartLayout;

    // Find nearest date index
    let closestIdx = 0;
    let closestDist = Infinity;
    allDates.forEach((_, i) => {
      const dist = Math.abs(xScale(i) - mouseX);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = i;
      }
    });

    if (closestDist > 40) {
      setTooltip(null);
      return;
    }

    const date = allDates[closestIdx];
    const values = series.map((s, sIdx) => {
      const point = (s.points || []).find((p) => p.date === date);
      return {
        variant: s.variant_name || s.variant_key,
        color: VARIANT_COLORS[sIdx % VARIANT_COLORS.length],
        value: point ? point[metric] : null,
      };
    });

    setTooltip({
      x: xScale(closestIdx),
      date,
      values,
    });
  };

  if (loading) {
    return (
      <div className="experiment-timeseries">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading timeline...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="experiment-timeseries">
        <div className="exp-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="experiment-timeseries">
      <div className="exp-ts-header">
        <div className="exp-ts-legend">
          {(data?.series || []).map((s, i) => (
            <div key={i} className="exp-ts-legend-item">
              <div
                className="exp-ts-legend-color"
                style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
              />
              <span>{s.variant_name || s.variant_key}</span>
            </div>
          ))}
        </div>
        <div className="exp-ts-controls">
          <button
            className={`exp-ts-toggle ${metric === 'conversion_rate' ? 'active' : ''}`}
            onClick={() => setMetric('conversion_rate')}
          >
            Conversion Rate
          </button>
          <button
            className={`exp-ts-toggle ${metric === 'revenue' ? 'active' : ''}`}
            onClick={() => setMetric('revenue')}
          >
            Revenue
          </button>
        </div>
      </div>

      <div className="exp-ts-chart" ref={containerRef}>
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltip(null)}
        />
        {tooltip && (
          <div
            className="exp-ts-tooltip"
            style={{ left: tooltip.x, top: 10 }}
          >
            <div className="exp-ts-tooltip-date">
              {new Date(tooltip.date).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })}
            </div>
            {tooltip.values.map((v, i) => (
              <div key={i} className="exp-ts-tooltip-row">
                <div className="exp-ts-tooltip-color" style={{ backgroundColor: v.color }} />
                <span className="exp-ts-tooltip-name">{v.variant}</span>
                <span className="exp-ts-tooltip-value">
                  {v.value != null
                    ? metric === 'conversion_rate'
                      ? (v.value * 100).toFixed(2) + '%'
                      : '$' + v.value.toFixed(2)
                    : '--'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(!data?.series || data.series.length === 0) && (
        <div className="exp-empty">
          <p>No timeline data available yet.</p>
        </div>
      )}
    </div>
  );
}

export default ExperimentTimeSeries;
